import React, { useCallback, useEffect, useState } from 'react';
import ReactTable from 'react-table';
import { css } from '@emotion/core';
import { useDispatch, useSelector } from 'react-redux';
import moment from 'moment';
import { useRequestState } from '@weave/alert-system';
import { TableStyles } from '../../../styles/table-style';
import 'react-table/react-table.css';

import { reactTableStyle } from '../../shared/server-data-table/react-table.style.js';
import { useDeepEffect } from '../../shared/hooks';
import { Page } from '../../shared';
import { dateFormat } from '../../../helpers/utils';
import {
  getLocationSettings,
  selectLocationSettings,
  LocationSettingsActionTypes,
} from '../../../redux/actions/location/location-settings/location-settings.action';
import { selectCurrentLocationId } from '../../../redux/actions/location/current-location';
import {
  PrimaryButton,
  TextField,
  useControlledField,
  MultiselectField,
  useForm,
} from '@weave/design-system';

interface LocationSettingsModel {
  set: string;
  key: string;
  value: string;
  source_id?: string;
}

const formatDates = (settings) =>
  settings.map((setting) => ({
    ...setting,
    created_at: moment(setting.created_at).format(dateFormat),
    modified_at: moment(setting.modified_at).format(dateFormat),
  }));

const columns = [
  {
    Header: 'Set',
    accessor: 'set',
  },
  {
    Header: 'Key',
    accessor: 'key',
  },
  {
    Header: 'Value',
    accessor: 'value',
    style: { whiteSpace: 'unset', wordWrap: 'break-word', display: 'inline' },
  },
  {
    Header: 'Modified at',
    accessor: 'modified_at',
  },
  {
    Header: 'Created at',
    accessor: 'created_at',
  },
  {
    Header: 'Source Id',
    accessor: 'source_id',
    style: { whiteSpace: 'unset', wordWrap: 'break-word' },
  },
];

const textFieldContainer = css`
  display: flex;
  justify-content: flex-start;
  align-items: center;
  gap: 20px;
`;

const searchField = css`
  flex: 0 0 30%;
`;

const autoCompleteDropdown = css`
  flex: 0 0 50%;
`;

export const LocationSettings = () => {
  const searchFieldProps = useControlledField({
    type: 'text',
    value: '',
    onChange: (value) => handleSearchChange(value),
  });

  const locationSettings: LocationSettingsModel[] = formatDates(
    useSelector(selectLocationSettings)
  );
  const { loading } = useRequestState(LocationSettingsActionTypes.Fetch);
  const locationId = useSelector(selectCurrentLocationId);

  const [searchText, setSearchText] = useState<string>('');
  const [filteredSettings, setFilteredSettings] = useState<LocationSettingsModel[]>([]);
  const dispatch = useDispatch();

  const {
    getFieldProps,
    values: { sourceId: selectedSourceIds },
  } = useForm({
    fields: {
      sourceId: {
        type: 'multiselect',
        value: [],
      },
    },
  });

  useEffect(() => {
    if (locationId) {
      dispatch(getLocationSettings(locationId));
    }
  }, [locationId, getLocationSettings]);

  useDeepEffect(() => {
    if (locationSettings) {
      setFilteredSettings(
        filterSettings(searchText, locationSettings, selectedSourceIds)
      );
    }
  }, [locationSettings, selectedSourceIds]);

  /**
   * Filters location settings based on:
   *  1. Search text in `set`, `key`, or `value`
   *  2. Selected source IDs from the dropdown
   */
  const filterSettings = useCallback(
    (
      searchText: string,
      locationSettingsList: LocationSettingsModel[],
      selectedIds: string[] = []
    ) => {
      let filtered = locationSettingsList;

      // Filter by text search
      if (searchText) {
        const lowerSearch = searchText.toLowerCase();
        filtered = filtered.filter(
          (setting) =>
            setting.set.toLowerCase().includes(lowerSearch) ||
            setting.key.toLowerCase().includes(lowerSearch) ||
            setting.value.toLowerCase().includes(lowerSearch)
        );
      }

      // Filter by selected SourceIds
      if (selectedIds && selectedIds.length > 0) {
        filtered = filtered.filter(
          (setting) => setting.source_id && selectedIds.includes(setting.source_id)
        );
      }

      return filtered;
    },
    []
  );

  const handleSearchChange = useCallback(
    (value: string) => {
      const filteredSettings = filterSettings(value, locationSettings, selectedSourceIds);
      setSearchText(value);
      setFilteredSettings(filteredSettings);
    },
    [locationSettings, selectedSourceIds]
  );

  const refresh = () => {
    dispatch(getLocationSettings(locationId));
  };

  return (
    <Page
      title="Locations"
      headerActions={
        <PrimaryButton
          size="small"
          css={css`
            max-width: 150px;
          `}
          onClick={refresh}
        >
          Refresh Data
        </PrimaryButton>
      }
    >
      <div css={textFieldContainer}>
        <div css={searchField}>
          <TextField
            name="search"
            label="Search in Set, Key and Value"
            autoComplete="off"
            {...searchFieldProps}
            clearable={true}
            value={searchText}
            css={css`
              min-height: 40px;
            `}
          />
        </div>
        <div css={autoCompleteDropdown}>
          <MultiselectField
            {...getFieldProps('sourceId')}
            label=""
            placeholder="Filter by Source Id"
          >
            {[...new Set(locationSettings?.map((s) => s?.source_id).filter(Boolean))].map(
              (location, i) => (
                <MultiselectField.Option key={i} value={location as string}>
                  {location}
                </MultiselectField.Option>
              )
            )}
          </MultiselectField>
        </div>
      </div>
      <ReactTable
        columns={columns}
        data={filteredSettings}
        loading={loading}
        defaultPageSize={20}
        css={[css(reactTableStyle), TableStyles]}
      />
    </Page>
  );
};
